import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { buildImageUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Upload, Trash2, ImagePlus, Loader2, Bell, ImageIcon, Coins, Tag, X, Plus, ChevronDown, Check, Search } from "lucide-react";
import { toast } from "sonner";
import RgSelect from "@/components/RgSelect";

/**
 * Modal with two tabs: Images (upload up to 3) + Alerts (branch-wise reorder thresholds).
 * Used from Product Master row "Settings" action.
 */
export default function ProductSettingsModal({ productId, productName, open, onOpenChange, onSaved }) {
  const [product, setProduct] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [levels, setLevels] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("images");
  const [rgSaving, setRgSaving] = useState(false);
  const [rgDraftIds, setRgDraftIds] = useState([]);
  const [rgDraftLabels, setRgDraftLabels] = useState({});
  // Supplier Details rows: { key, supplier_id, supplier_name, alias_name, rate }
  const [supplierRows, setSupplierRows] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]); // [{id, name}] from RM Vendors
  const [aliasSaving, setAliasSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !productId) return;
    (async () => {
      setLoading(true);
      try {
        const [p, b, v] = await Promise.all([
          api.get(`/products/${productId}`),
          api.get("/branches?only_active=true"),
          api.get("/production/rm-vendors"),
        ]);
        setProduct(p.data);
        setBranches(b.data);
        setVendorOptions((v.data || []).map((x) => ({ id: x.id, name: x.name })));
        const init = {};
        const stored = p.data.branch_reorder_levels || {};
        b.data.forEach((br) => {
          init[br.code] = stored[br.code] ?? p.data.reorder_level;
        });
        setLevels(init);
        const rgList = p.data.rg_parents || (p.data.rg_parent_id ? [{ id: p.data.rg_parent_id, name: p.data.rg_parent_name || "" }] : []);
        setRgDraftIds(rgList.map((r) => r.id));
        setRgDraftLabels(Object.fromEntries(rgList.map((r) => [r.id, r.name])));
        // Supplier Details rows: Wings-synced suppliers come FIRST (alias/rate
        // pre-filled from any saved values), followed by manually-linked ones.
        const aliasById = {};
        (p.data.supplier_aliases || []).forEach((a) => {
          if (a?.supplier_id) aliasById[a.supplier_id] = a;
        });
        const rows = [];
        let n = 0;
        (p.data.suppliers || []).forEach((s) => {
          const a = aliasById[s.id] || {};
          rows.push({
            key: `r${n++}`,
            supplier_id: s.id,
            supplier_name: s.name,
            alias_name: a.alias_name || "",
            rate: a.rate ?? "",
          });
          delete aliasById[s.id];
        });
        // Manually-added suppliers (from RM Vendors) that aren't in the synced list.
        Object.values(aliasById).forEach((a) => {
          rows.push({
            key: `r${n++}`,
            supplier_id: a.supplier_id,
            supplier_name: a.supplier_name || "",
            alias_name: a.alias_name || "",
            rate: a.rate ?? "",
          });
        });
        setSupplierRows(rows);
      } finally {
        setLoading(false);
      }
    })();
  }, [productId, open]);

  const refresh = async () => {
    const res = await api.get(`/products/${productId}`);
    setProduct(res.data);
  };

  const handleUpload = async (fileList) => {
    // fileList can be a FileList (drop) OR a change-event value.
    const files = Array.from(fileList || []).filter((f) => f && f.size > 0);
    if (files.length === 0) return;

    const room = 3 - (product?.images?.length || 0);
    const toUpload = files.slice(0, room);
    if (files.length > room) {
      toast.warning(`Only ${room} slot(s) left — first ${room} file(s) will be uploaded.`);
    }

    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of toUpload) {
      // Client-side validation
      if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
        toast.error(`Skipped ${file.name}: unsupported format`);
        fail++; continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Skipped ${file.name}: >5 MB`);
        fail++; continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/products/${productId}/images`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (err) {
        fail++;
        toast.error(`${file.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
    }
    if (ok > 0) toast.success(`${ok} image${ok > 1 ? "s" : ""} uploaded to Bunny CDN`);
    await refresh();
    onSaved?.();
    setUploading(false);
  };

  const onInputChange = (e) => {
    handleUpload(e.target.files);
    e.target.value = "";
  };

  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const deleteImage = async (imgId) => {
    try {
      await api.delete(`/products/${productId}/images/${imgId}`);
      toast.success("Image removed");
      await refresh();
      onSaved?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const saveLevels = async () => {
    setSaving(true);
    try {
      const payload = { levels: Object.fromEntries(Object.entries(levels).map(([k, v]) => [k, Math.max(0, parseInt(v, 10) || 0)])) };
      await api.put(`/products/${productId}/branch-reorder-levels`, payload);
      toast.success("Branch reorder levels saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveRg = async () => {
    setRgSaving(true);
    try {
      await api.patch(`/products/${productId}/rg-parent`, { rg_parent_ids: rgDraftIds });
      toast.success(rgDraftIds.length ? `Mapped to ${rgDraftIds.length} Raw-Gold product${rgDraftIds.length === 1 ? "" : "s"}` : "Raw-Gold mappings cleared");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setRgSaving(false);
    }
  };

  const saveAliases = async () => {
    setAliasSaving(true);
    try {
      const aliases = supplierRows
        .filter((r) => r.supplier_id)
        .map((r) => ({
          supplier_id: r.supplier_id,
          alias_name: (r.alias_name || "").trim(),
          rate: r.rate === "" || r.rate === null || r.rate === undefined ? null : Number(r.rate),
        }))
        .filter((r) => r.alias_name || r.rate !== null);
      await api.put(`/products/${productId}/supplier-aliases`, { aliases });
      toast.success(aliases.length ? `Saved details for ${aliases.length} supplier${aliases.length === 1 ? "" : "s"}` : "Supplier details cleared");
      await refresh();
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setAliasSaving(false);
    }
  };

  const updateSupplierRow = (key, patch) => {
    setSupplierRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const removeSupplierRow = (key) => {
    setSupplierRows((rows) => rows.filter((r) => r.key !== key));
  };
  const addSupplierRow = () => {
    setSupplierRows((rows) => [...rows, { key: `r${Date.now()}`, supplier_id: "", supplier_name: "", alias_name: "", rate: "" }]);
  };

  const images = product?.images || [];
  const remaining = 3 - images.length;
  const defaultLevel = product?.reorder_level || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Product Settings</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-gray-900">{productName}</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#9B1C1C]" />
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="images" data-testid="settings-tab-images">
                <ImageIcon className="h-3.5 w-3.5 mr-1.5" /> Images ({images.length}/3)
              </TabsTrigger>
              <TabsTrigger value="alerts" data-testid="settings-tab-alerts">
                <Bell className="h-3.5 w-3.5 mr-1.5" /> Branch Alerts
              </TabsTrigger>
              <TabsTrigger value="rg" data-testid="settings-tab-rg">
                <Coins className="h-3.5 w-3.5 mr-1.5" /> Raw Material
              </TabsTrigger>
              <TabsTrigger value="aliases" data-testid="settings-tab-aliases">
                <Tag className="h-3.5 w-3.5 mr-1.5" /> Supplier Details ({supplierRows.filter((r) => r.supplier_id).length})
              </TabsTrigger>
            </TabsList>

            {/* Images */}
            <TabsContent value="images" className="space-y-4 pt-4">
              <p className="text-sm text-gray-600">
                Upload up to 3 images — hosted on Bunny CDN for fast global delivery. Drag &amp; drop multiple files at once.
              </p>

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`rounded-md border-2 border-dashed transition-colors p-4 ${
                  dragOver ? "border-[#9B1C1C] bg-red-50/40" : "border-gray-200 bg-gray-50/40"
                }`}
                data-testid="settings-image-dropzone"
              >
                <div className="grid grid-cols-3 gap-3">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={`aspect-square rounded-md overflow-hidden border-2 relative group ${
                        images[i] ? "border-gray-200 bg-white" : "border-dashed border-gray-200 bg-white/60"
                      }`}
                    >
                      {images[i] ? (
                        <>
                          <img src={buildImageUrl(images[i].cdn_url || images[i].storage_path)} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => deleteImage(images[i].id)}
                            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/95 text-red-700 flex items-center justify-center opacity-0 group-hover:opacity-100 shadow"
                            aria-label="Delete image"
                            data-testid={`settings-image-delete-${images[i].id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-1">
                          <ImagePlus className="h-6 w-6" />
                          <span className="text-[10px] font-semibold uppercase tracking-widest">Slot {i + 1}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-center text-xs text-gray-500 mt-3">
                  {dragOver ? "Drop to upload" : "Drag & drop images here, or use the button below"}
                </p>
              </div>

              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                onChange={onInputChange}
                className="hidden"
                data-testid="settings-image-upload-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading || remaining === 0}
                className="w-full bg-gray-900 hover:bg-gray-800"
                data-testid="settings-image-upload-button"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {remaining === 0 ? "All slots used" : `Choose Files (${remaining} slot${remaining > 1 ? "s" : ""} left)`}
                  </>
                )}
              </Button>
            </TabsContent>

            {/* Branch alert levels */}
            <TabsContent value="alerts" className="space-y-4 pt-4">
              <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-amber-800 mb-1">
                  Branch-wise Reorder Levels
                </p>
                <p className="text-xs text-amber-900 leading-relaxed">
                  Stock below this threshold triggers a low-stock alert for that branch and feeds the Procurement Planner.
                  Default reorder level on the product master is <span className="font-mono font-semibold">{defaultLevel}</span>.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {branches.map((b) => (
                  <div key={b.code} className="flex items-center gap-3 p-3 rounded-md border border-gray-200 hover:border-gray-300 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{b.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{b.code} · {b.city}</p>
                    </div>
                    <div className="w-24">
                      <Input
                        type="number"
                        min={0}
                        value={levels[b.code] ?? ""}
                        onChange={(e) => setLevels({ ...levels, [b.code]: e.target.value })}
                        className="h-9 text-right font-mono"
                        data-testid={`settings-level-${b.code}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const all = {};
                    branches.forEach((b) => (all[b.code] = defaultLevel));
                    setLevels(all);
                  }}
                  data-testid="settings-level-reset"
                >
                  Reset all to default ({defaultLevel})
                </Button>
              </div>
            </TabsContent>

            {/* Raw Material (Rg) mapping */}
            <TabsContent value="rg" className="space-y-4 pt-4">
              <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-amber-800 mb-1">
                  Raw-Gold Parents
                </p>
                <p className="text-xs text-amber-900 leading-relaxed">
                  Link this product to one or more Raw-Gold (Rg) items it is
                  manufactured from. Selected items appear as chips below the picker
                  &mdash; use the &times; on each chip to remove it. The mapped
                  Rg stock is shown on the Stock Monitoring grid and branch matrix.
                </p>
              </div>

              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5 block">
                  Select Raw Materials (Rg)
                </label>
                <RgSelect
                  multi
                  value={rgDraftIds}
                  valueLabel={rgDraftLabels}
                  onChange={(ids, names) => {
                    setRgDraftIds(ids);
                    setRgDraftLabels((cur) => {
                      const next = { ...cur };
                      ids.forEach((id, i) => { if (names[i]) next[id] = names[i]; });
                      return next;
                    });
                  }}
                />
              </div>
            </TabsContent>

            {/* Supplier Details — per-supplier alias name + buying rate */}
            <TabsContent value="aliases" className="space-y-4 pt-4">
              <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-blue-800 mb-1">
                  Supplier Details (alias name + rate)
                </p>
                <p className="text-xs text-blue-900 leading-relaxed">
                  Our own name <span className="font-semibold">"{productName}"</span> stays the prime name.
                  Each supplier may label the same product differently on their invoice — record their name and
                  your buying rate here so the team can search by <em>either</em> name and see the agreed rate.
                  Wings-synced suppliers are listed first; use <span className="font-semibold">Add Supplier</span> for more.
                </p>
              </div>

              <div className="space-y-2" data-testid="settings-aliases-list">
                {supplierRows.map((row) => (
                  <div
                    key={row.key}
                    className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_110px_32px] items-center gap-2 p-2 rounded-md border border-gray-200"
                    data-testid={`settings-supplier-row-${row.key}`}
                  >
                    <SupplierPicker
                      options={vendorOptions}
                      value={row.supplier_id}
                      valueLabel={row.supplier_name}
                      onChange={(sid, sname) => updateSupplierRow(row.key, { supplier_id: sid, supplier_name: sname })}
                      testId={`settings-supplier-select-${row.key}`}
                    />
                    <Input
                      type="text"
                      value={row.alias_name}
                      onChange={(e) => updateSupplierRow(row.key, { alias_name: e.target.value })}
                      placeholder="Supplier's product name"
                      className="h-9"
                      data-testid={`settings-alias-input-${row.key}`}
                    />
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={row.rate}
                      onChange={(e) => updateSupplierRow(row.key, { rate: e.target.value })}
                      placeholder="Rate ₹"
                      className="h-9 text-right font-mono"
                      data-testid={`settings-rate-input-${row.key}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeSupplierRow(row.key)}
                      className="h-8 w-8 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
                      aria-label="Remove supplier row"
                      data-testid={`settings-supplier-remove-${row.key}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {supplierRows.length === 0 && (
                  <p className="text-center text-sm text-gray-500 py-4">
                    No suppliers yet — add one below to record their name & rate for this product.
                  </p>
                )}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={addSupplierRow}
                className="w-full border-dashed"
                data-testid="settings-add-supplier-row"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add Supplier
              </Button>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {tab === "alerts" && (
            <Button
              onClick={saveLevels}
              disabled={saving}
              className="bg-[#9B1C1C] hover:bg-[#7F1D1D]"
              data-testid="settings-save-alerts"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Alerts"}
            </Button>
          )}
          {tab === "rg" && (
            <Button
              onClick={saveRg}
              disabled={rgSaving}
              className="bg-[#9B1C1C] hover:bg-[#7F1D1D]"
              data-testid="settings-save-rg"
            >
              {rgSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Mapping"}
            </Button>
          )}
          {tab === "aliases" && (
            <Button
              onClick={saveAliases}
              disabled={aliasSaving}
              className="bg-[#9B1C1C] hover:bg-[#7F1D1D]"
              data-testid="settings-save-aliases"
            >
              {aliasSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Supplier Details"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Compact type-ahead supplier picker used per-row in the Supplier Details tab. */
function SupplierPicker({ options, value, valueLabel, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) => o.name.toLowerCase().includes(query));
  }, [q, options]);

  const label = useMemo(() => {
    if (!value) return "Select supplier…";
    const hit = options.find((o) => o.id === value);
    return hit ? hit.name : (valueLabel || "Selected supplier");
  }, [value, valueLabel, options]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="h-9 w-full justify-between font-normal text-left"
          data-testid={testId}
        >
          <span className={value ? "text-gray-900 truncate" : "text-gray-400"}>{label}</span>
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              autoFocus
              placeholder="Search supplier…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 h-9"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {filtered.map((o) => (
            <button
              type="button"
              key={o.id}
              onClick={() => { onChange(o.id, o.name); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-gray-50 ${
                value === o.id ? "font-semibold text-[#9B1C1C]" : "text-gray-700"
              }`}
              data-testid={testId ? `${testId}-option-${o.id}` : undefined}
            >
              {value === o.id ? <Check className="h-3.5 w-3.5" /> : <span className="w-3.5" />}
              <span className="truncate">{o.name}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-xs text-gray-500 text-center">No matches</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
